/**
 * Vuex module for managing gesture alerts.
 * Alerts are filtered by the analysis pipeline (automatic baseline); no sensitivity or thresholds in front/back.
 */

const state = {
  // Filtered alerts (array of {time, gesture_type, gesture_name, magnitude})
  filteredAlerts: [],
  // Store key the alerts belong to: the analysisData composite key
  // '<sourceType>:<sourceId>:<runId|active>' (legacy callers may still pass a numeric id).
  analysisId: null,
};

function normalizeGestureType(rawType, magnitude = null) {
  const value = String(rawType || '').trim().toLowerCase();
  if (value === 'gesture' || value === 'alert' || value === 'red') {
    return 'gesture';
  }
  if (value === 'sustained' || value === 'warning' || value === 'orange') {
    return 'sustained';
  }

  const numericMagnitude = Number(magnitude);
  if (Number.isFinite(numericMagnitude)) {
    return numericMagnitude >= 80 ? 'gesture' : 'sustained';
  }
  return 'sustained';
}

const getters = {
  /**
   * Get all filtered alerts
   */
  getFilteredAlerts: (state) => state.filteredAlerts,
  
  /**
   * Get alerts at a specific time (in seconds)
   * Returns array of alerts at that time
   */
  getAlertsAtTime: (state) => (timeInSeconds) => {
    if (!state.filteredAlerts || state.filteredAlerts.length === 0) {
      return [];
    }
    
    // Parse time string to seconds for comparison
    const parseTime = (timeStr) => {
      if (!timeStr) return 0;
      const parts = timeStr.split(':');
      if (parts.length !== 3) return 0;
      const minutes = parseInt(parts[0], 10) || 0;
      const seconds = parseInt(parts[1], 10) || 0;
      const milliseconds = parseInt(parts[2], 10) || 0;
      return minutes * 60 + seconds + milliseconds / 1000;
    };
    
    // Find alerts within 0.5 seconds (one frame interval)
    // Use tolerance to account for floating point precision
    const tolerance = 0.5;
    return state.filteredAlerts.filter((alert) => {
      const alertTime = parseTime(alert.time);
      const diff = Math.abs(alertTime - timeInSeconds);
      return diff < tolerance;
    });
  },
  
  /**
   * Get alert level for a specific time
   * Returns: 'alert' (red), 'warning' (orange), or 'normal'
   */
  getAlertLevelAtTime: (state, getters) => (timeInSeconds) => {
    const alertsAtTime = getters.getAlertsAtTime(timeInSeconds);
    
    if (alertsAtTime.length === 0) {
      return 'normal';
    }
    
    // Check if any alert is a gesture (red alert)
    const hasGesture = alertsAtTime.some(
      (alert) => normalizeGestureType(alert?.gesture_type, alert?.magnitude) === 'gesture'
    );
    if (hasGesture) {
      return 'alert';
    }
    
    // Check if any alert is sustained (orange warning)
    const hasSustained = alertsAtTime.some(
      (alert) => normalizeGestureType(alert?.gesture_type, alert?.magnitude) === 'sustained'
    );
    if (hasSustained) {
      return 'warning';
    }
    
    return 'normal';
  },
  
  /**
   * Get landmark colors based on alert level at time
   * Returns: {face: 'red'|'orange'|'normal', hands: 'red'|'orange'|'normal'}
   */
  getLandmarkColorsAtTime: (state, getters) => (timeInSeconds) => {
    const alertLevel = getters.getAlertLevelAtTime(timeInSeconds);
    
    if (alertLevel === 'alert') {
      return { face: 'red', hands: 'red' };
    } else if (alertLevel === 'warning') {
      return { face: 'orange', hands: 'orange' };
    }
    
    return { face: 'normal', hands: 'normal' };
  },
  
  /**
   * Get analysis ID (composite analysisKey, or the numeric id for legacy callers)
   */
  getAnalysisId: (state) => state.analysisId,

  /**
   * True when the loaded alerts belong to the given store key (guards against showing the
   * alerts of another run of the same video).
   */
  isForAnalysis: (state) => (analysisId) =>
    state.analysisId != null && analysisId != null && String(state.analysisId) === String(analysisId),
};

const mutations = {
  SET_FILTERED_ALERTS(state, alerts) {
    state.filteredAlerts = Array.isArray(alerts)
      ? alerts.map((alert) => ({
        ...alert,
        gesture_type: normalizeGestureType(alert?.gesture_type, alert?.magnitude),
      }))
      : [];
  },
  SET_ANALYSIS_ID(state, analysisId) {
    state.analysisId = analysisId;
  },
  CLEAR_ALERTS(state) {
    state.filteredAlerts = [];
    state.analysisId = null;
  },
};

const actions = {
  /**
   * Set filtered alerts (from analysis pipeline; no sensitivity/baseline in front or back).
   */
  setFilteredAlerts({ commit }, { alerts, analysisId }) {
    commit('SET_FILTERED_ALERTS', alerts);
    commit('SET_ANALYSIS_ID', analysisId);
  },
  /**
   * Clear all alerts (e.g., when switching to different analysis)
   */
  clearAlerts({ commit }) {
    commit('CLEAR_ALERTS');
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
